import React from 'react';
import type { SourceType, ViewMode } from '../../domain/types';
import { ZuneloMark } from './ZuneloMark';

export type BrandType =
  | SourceType
  | ViewMode
  | 'radar'
  | 'gmail'
  | 'agents'
  | 'robot'
  | 'zunelo'
  | 'notes'
  | 'reminders'
  | 'gemini';
export type BrandIconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface BrandIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  brand?: BrandType;
  source?: BrandType;
  size?: BrandIconSize | number;
  monochrome?: boolean;
  alt?: string;
}

export const BrandIcon: React.FC<BrandIconProps> = ({
  brand,
  source,
  size = 'md',
  monochrome = false,
  alt,
  className = '',
  style,
  ...props
}) => {
  const sizeMap: Record<BrandIconSize, number> = {
    xs: 14,
    sm: 18,
    md: 22,
    lg: 28,
    xl: 36,
  };

  const px = typeof size === 'number' ? size : sizeMap[size] || sizeMap.md;

  const rawBrand = brand || source || 'radar';
  const brandName = (typeof rawBrand === 'string' ? rawBrand : 'radar').toLowerCase();
  const title = alt || brandName.charAt(0).toUpperCase() + brandName.slice(1);

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${px}px`,
    height: `${px}px`,
    flexShrink: 0,
    userSelect: 'none',
    ...style,
  };

  const renderIcon = () => {
    switch (brandName) {
      case 'zunelo':
        return <ZuneloMark size={px} title={title} />;

      case 'jira':
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill={monochrome ? 'currentColor' : '#0052CC'}
            role="img"
            aria-label={title}
          >
            <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0Z" />
          </svg>
        );

      case 'github':
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill={monochrome ? 'currentColor' : 'var(--z-text)'}
            role="img"
            aria-label={title}
          >
            <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
          </svg>
        );

      case 'wiki':
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={title}
          >
            <path d="M4 5a2 2 0 0 1 2-2h13v18H6a2 2 0 0 1-2-2V5Z" />
            <path d="M4 17a2 2 0 0 0 2 2h13M8 7h7M8 11h7" />
          </svg>
        );

      case 'slack':
        if (monochrome) {
          return (
            <svg
              viewBox="0 0 24 24"
              width={px}
              height={px}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              role="img"
              aria-label={title}
            >
              <rect width="18" height="18" x="3" y="3" rx="4" />
              <path d="M7 10h10M10 7v10M14 7v10M7 14h10" />
            </svg>
          );
        }
        return (
          <img
            src="/brand/slack-mark.png"
            alt={title}
            width={px}
            height={px}
            style={{ objectFit: 'contain' }}
          />
        );

      case 'gemini':
        return (
          <svg viewBox="0 0 24 24" width={px} height={px} role="img" aria-label={title}>
            <defs>
              <linearGradient id="gemini-gradient" x1="3" y1="3" x2="21" y2="21">
                <stop stopColor="#4285F4" />
                <stop offset="0.55" stopColor="#8E5CF7" />
                <stop offset="1" stopColor="#D965E8" />
              </linearGradient>
            </defs>
            <path
              d="M12 2.6c1.64 5.76 3.64 7.76 9.4 9.4-5.76 1.64-7.76 3.64-9.4 9.4-1.64-5.76-3.64-7.76-9.4-9.4 5.76-1.64 7.76-3.64 9.4-9.4Z"
              fill="url(#gemini-gradient)"
            />
          </svg>
        );

      case 'gmail':
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill="none"
            stroke={monochrome ? 'currentColor' : '#EA4335'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={title}
          >
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        );

      case 'agents':
      case 'robot':
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill="none"
            stroke={monochrome ? 'currentColor' : 'var(--z-accent-3)'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={title}
          >
            <rect x="3" y="11" width="18" height="10" rx="3" />
            <circle cx="12" cy="5" r="2" fill={monochrome ? 'currentColor' : 'var(--z-accent-3)'} />
            <path d="M12 7v4" />
            <line x1="8" y1="16" x2="8.01" y2="16" strokeWidth="2.5" />
            <line x1="16" y1="16" x2="16.01" y2="16" strokeWidth="2.5" />
          </svg>
        );

      case 'radar':
      default:
        return (
          <svg
            viewBox="0 0 24 24"
            width={px}
            height={px}
            fill="none"
            stroke={monochrome ? 'currentColor' : 'var(--z-accent)'}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label={title}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a10 10 0 0 1 10 10" />
            <path d="M12 6a6 6 0 0 1 6 6" />
            <circle cx="12" cy="12" r="2" />
          </svg>
        );
    }
  };

  return (
    <span
      className={`z-brand-icon z-brand-${brandName} ${className}`}
      style={containerStyle}
      {...props}
    >
      {renderIcon()}
    </span>
  );
};
